// Form state <-> contract input, and the window-independence facts (NED-*, §10.3).
//
// Pure. No React, no Convex, no clock read except where a caller passes one in.
//
// ── VALIDATION IS BORROWED, NEVER REWRITTEN ─────────────────────────────────────
//
// Two rules apply to a help window and this module owns neither:
//
//   the shape of the input  -> `@herefor/contracts`' zod schemas, the same ones the
//                              Convex functions enforce;
//   the overnight refusal   -> `@herefor/domain-kernel`'s `validateWindow`, the same
//                              function `convex/domain/needs.ts` calls.
//
// AVL-007 is why the second one matters here rather than only on the server. An
// overnight window is REJECTED, never split (plan §11.6), and a person who typed one
// deserves to be told immediately and in the product's own words rather than after a
// round trip. Calling the kernel means the client and the server cannot disagree
// about where midnight is — which, in a zone with daylight saving, is not a trivial
// agreement to maintain by hand.
//
// ── THE WINDOW IS THE NEED'S OWN ────────────────────────────────────────────────
//
// `suggestWindowFor` pre-fills a new need from its event, and deliberately does NOT
// copy it: it opens an hour before and closes two hours after, because the whole
// point of the canonical case is that a driver for a 10:15 appointment is needed from
// 09:15 until 13:00. A pre-fill that matched the event exactly would teach the
// opposite of what NED-002 requires, on the one screen where the lesson lands.

import { needs as needContracts } from "@herefor/contracts";
import {
  instant,
  resolveWallClock,
  toWallClock,
  validateWindow,
  type HelpMode,
  type Instant,
  type TimeZoneId,
  type WindowRejection,
} from "@herefor/domain-kernel";

/** What the form holds while somebody is typing. */
export interface NeedFormValues {
  readonly label: string;
  readonly needType: "visit" | "drive" | "accompany" | "call" | "errand" | "other";
  /** `YYYY-MM-DDTHH:mm` local — the HELP window, never the event's. */
  readonly startsAtLocal: string;
  readonly endsAtLocal: string;
  readonly requiredCapacity: string;
  readonly requiredHelpModes: readonly HelpMode[];
  readonly importance: "ordinary" | "important" | "critical";
  readonly meetingDetail: string;
  readonly practicalNote: string;
}

export const EMPTY_NEED_FORM: NeedFormValues = {
  label: "",
  needType: "visit",
  startsAtLocal: "",
  endsAtLocal: "",
  requiredCapacity: "1",
  requiredHelpModes: [],
  importance: "ordinary",
  meetingDetail: "",
  practicalNote: "",
};

export interface FieldProblem {
  readonly field: string;
  readonly message: string;
}

export type FormValidation<T> =
  | { readonly ok: true; readonly input: T }
  | { readonly ok: false; readonly problems: readonly FieldProblem[] };

/**
 * The PARSED shape of a contract input, not the raw one.
 *
 * `needContracts.CreateNeedInput` is `z.input`, which leaves the branded fields —
 * instants, capacities — as `unknown`, because that is what the schema accepts before
 * it narrows them. What a caller actually holds after `safeParse` succeeds is the
 * OUTPUT, and that is what goes to the mutation. Derived from `parse`'s return type
 * so it stays the contract's answer rather than a hand-written copy that can drift.
 */
export type CreateNeedParsed = ReturnType<typeof needContracts.createNeedInput.parse>;
export type UpdateNeedParsed = ReturnType<typeof needContracts.updateNeedInput.parse>;

/** A need as a detail surface renders it. Coverage arrives separately (COV-002). */
export interface NeedDetailView {
  readonly supportNeedId: string;
  readonly eventId: string | null;
  readonly label: string;
  readonly needType: NeedFormValues["needType"];
  readonly startsAt: number;
  readonly endsAt: number;
  readonly windowLabel: string;
  readonly requiredCapacity: number;
  readonly requiredHelpModes: readonly HelpMode[];
  readonly importance: NeedFormValues["importance"];
  readonly meetingDetail: string | null;
  readonly practicalNote: string | null;
  readonly state: "draft" | "open" | "completed" | "cancelled";
  readonly revision: number;
  readonly isPast: boolean;
}

/* ============================================================== form -> input === */

export function toCreateInput(
  values: NeedFormValues,
  circleId: string,
  zone: TimeZoneId,
  eventId?: string,
): FormValidation<CreateNeedParsed> {
  const window = readWindow(values, zone);
  if (!window.ok) return window;

  const parsed = needContracts.createNeedInput.safeParse({
    circleId,
    ...(eventId === undefined ? {} : { eventId }),
    label: values.label.trim(),
    needType: values.needType,
    startsAt: window.startsAt,
    endsAt: window.endsAt,
    requiredCapacity: Number(values.requiredCapacity),
    requiredHelpModes: values.requiredHelpModes,
    importance: values.importance,
    ...optionalText("meetingDetail", values.meetingDetail),
    ...optionalText("practicalNote", values.practicalNote),
  });

  return parsed.success
    ? { ok: true, input: parsed.data }
    : { ok: false, problems: problemsFrom(parsed.error.issues) };
}

export function toUpdateInput(
  values: NeedFormValues,
  original: NeedDetailView,
  circleId: string,
  zone: TimeZoneId,
): FormValidation<UpdateNeedParsed> {
  const window = readWindow(values, zone);
  if (!window.ok) return window;

  const parsed = needContracts.updateNeedInput.safeParse({
    circleId,
    supportNeedId: original.supportNeedId,
    expectedRevision: original.revision,
    label: values.label.trim(),
    needType: values.needType,
    startsAt: window.startsAt,
    endsAt: window.endsAt,
    requiredCapacity: Number(values.requiredCapacity),
    requiredHelpModes: values.requiredHelpModes,
    importance: values.importance,
    meetingDetail: clearable(values.meetingDetail),
    practicalNote: clearable(values.practicalNote),
  });

  return parsed.success
    ? { ok: true, input: parsed.data }
    : { ok: false, problems: problemsFrom(parsed.error.issues) };
}

/* ================================================== the window-independence bits === */

/**
 * How this need's window relates to its event's.
 *
 * Not a validation — both answers are legitimate. It exists so the form can say
 * something true and useful at the moment the organiser is most likely to have
 * confused the two ideas, which is the risk NED-010 and this bead both name.
 */
export type WindowRelation = "wider" | "identical" | "narrower" | "unrelated";

export function relateToEvent(
  need: { readonly startsAt: number; readonly endsAt: number },
  event: { readonly startsAt: number; readonly endsAt: number } | null,
): WindowRelation {
  if (event === null) return "unrelated";
  if (need.startsAt === event.startsAt && need.endsAt === event.endsAt) return "identical";
  if (need.startsAt <= event.startsAt && need.endsAt >= event.endsAt) return "wider";
  return "narrower";
}

/**
 * A starting window for a need attached to an event.
 *
 * An hour before and two hours after — the canonical case's shape, not the event's
 * times. Pre-filling with the event window exactly would teach precisely the mistake
 * NED-002 exists to prevent, on the one screen where somebody is deciding.
 *
 * Falls back to the event's own window if the padded one would cross midnight, since
 * AVL-007 refuses that outright and a pre-fill that cannot be saved is worse than a
 * modest one.
 */
export function suggestWindowFor(
  event: { readonly startsAt: number; readonly endsAt?: number },
  zone: TimeZoneId,
): { readonly startsAtLocal: string; readonly endsAtLocal: string } {
  const HOUR = 3_600_000;
  const endsAt = event.endsAt ?? event.startsAt + HOUR;
  const padded = { startsAt: instant(event.startsAt - HOUR), endsAt: instant(endsAt + 2 * HOUR) };

  const usable =
    validateWindow(padded, zone).kind === "ok"
      ? padded
      : { startsAt: instant(event.startsAt), endsAt: instant(endsAt) };

  return {
    startsAtLocal: toLocalInputValue(usable.startsAt, zone),
    endsAtLocal: toLocalInputValue(usable.endsAt, zone),
  };
}

/* ------------------------------------------------------------------ plumbing --- */

interface ReadWindow {
  readonly ok: true;
  readonly startsAt: number;
  readonly endsAt: number;
}

/**
 * Read both ends of the help window, refusing what V1 refuses.
 *
 * Returns the kernel's rejection KIND as the problem's field-level message key, so
 * the surface can look up the reviewed sentence in `WINDOW_REJECTION_MESSAGES`
 * rather than this module inventing one.
 */
function readWindow(
  values: NeedFormValues,
  zone: TimeZoneId,
): ReadWindow | { readonly ok: false; readonly problems: readonly FieldProblem[] } {
  const startsAt = parseLocal(values.startsAtLocal, zone);
  const endsAt = parseLocal(values.endsAtLocal, zone);

  const problems: FieldProblem[] = [];
  if (startsAt === null) problems.push({ field: "startsAt", message: UNREADABLE_TIME });
  if (endsAt === null) problems.push({ field: "endsAt", message: UNREADABLE_TIME });
  if (startsAt === null || endsAt === null) return { ok: false, problems };

  // The kernel's own rule, not a copy of it: same function the backend calls, so the
  // two cannot disagree about where midnight falls in a zone that observes DST.
  const validated = validateWindow({ startsAt: instant(startsAt), endsAt: instant(endsAt) }, zone);
  if (validated.kind === "rejected") {
    return {
      ok: false,
      problems: [{ field: windowFieldFor(validated.rejection), message: validated.rejection.kind }],
    };
  }

  return { ok: true, startsAt, endsAt };
}

/** Which end of the window a rejection belongs against. */
function windowFieldFor(rejection: WindowRejection): string {
  return rejection.kind === "empty_window" ? "endsAt" : "endsAt";
}

/**
 * A developer-facing fallback, and deliberately terse.
 *
 * It is reachable only when a browser hands back a `datetime-local` value that is not
 * a datetime, which no real input does. Everything a person can actually cause is
 * either a contract message or a `WINDOW_REJECTION_MESSAGES` entry.
 */
const UNREADABLE_TIME = "empty_window";

export function parseLocal(raw: string, zone: TimeZoneId): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/u.exec(raw.trim());
  if (match === null) return null;

  const [, year, month, day, hour, minute] = match;
  const resolved = resolveWallClock(
    {
      year: Number(year),
      month: Number(month),
      day: Number(day),
      hour: Number(hour),
      minute: Number(minute),
    },
    zone,
  );

  // A DST gap is not a time, and an ambiguous reading is two — picking one for
  // somebody is the bug R-024 rates Critical, so the form is told to ask again.
  return resolved.kind === "ok" ? resolved.instant : null;
}

export function toLocalInputValue(at: Instant, zone: TimeZoneId): string {
  const wall = toWallClock(at, zone);
  const pad = (value: number, width = 2) => String(value).padStart(width, "0");
  return `${pad(wall.year, 4)}-${pad(wall.month)}-${pad(wall.day)}T${pad(wall.hour)}:${pad(wall.minute)}`;
}

function optionalText(field: string, raw: string): Record<string, string> {
  const trimmed = raw.trim();
  return trimmed === "" ? {} : { [field]: trimmed };
}

function clearable(raw: string): string | null {
  const trimmed = raw.trim();
  return trimmed === "" ? null : trimmed;
}

function problemsFrom(
  issues: readonly { readonly path: readonly PropertyKey[]; readonly message: string }[],
): readonly FieldProblem[] {
  return issues.map((issue) => ({
    field: issue.path.length === 0 ? "form" : String(issue.path[issue.path.length - 1]),
    message: issue.message,
  }));
}
